import React, { useRef } from 'react';
import { motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import { EASE_OUT } from './variants';

type Props = {
  src: string;
  alt: string;
  className?: string;
  /** Total vertical drift in px across the scroll range. */
  amount?: number;
  loading?: 'lazy' | 'eager';
  srcSet?: string;
};

/**
 * Drop-in replacement for <img> that fades + scales in on first view
 * and drifts gently with scroll (parallax). Because it renders the
 * <img> itself — no wrapper — it never disturbs layout. Static under
 * reduced motion.
 */
const ParallaxImage: React.FC<Props> = ({
  src,
  alt,
  className,
  amount = 28,
  loading,
  srcSet,
}) => {
  const prefersReducedMotion = useReducedMotion();
  const ref = useRef<HTMLImageElement | null>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [amount, -amount]);

  if (prefersReducedMotion) {
    return (
      <img ref={ref} src={src} srcSet={srcSet} alt={alt} className={className} loading={loading} />
    );
  }

  return (
    <motion.img
      ref={ref}
      src={src}
      srcSet={srcSet}
      alt={alt}
      className={className}
      loading={loading}
      style={{ y, willChange: 'transform' }}
      initial={{ opacity: 0, scale: 1.06 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.85, ease: EASE_OUT }}
    />
  );
};

export default ParallaxImage;
